/**
 * The admin app-manifest routes the Installed Apps page calls, through api-client so the Firebase token travels with
 * each request. Activation and disabling change the tenant schema, so callers refetch the schema afterwards.
 */
import { apiFetch } from '@diggens/api-client';
import type { TenantAppRecord } from './installedApps';

export interface ActivateAppResponse {
  app: TenantAppRecord;
  picklistsCreated: string[];
  picklistsExtended: Record<string, string[]>;
  replacedVersion: number | null;
}

export async function fetchInstalledApps(): Promise<TenantAppRecord[]> {
  const response = await apiFetch<{ apps: TenantAppRecord[] }>('/apps/manifests');
  return response.apps;
}

export function activateAppVersion(id: string): Promise<ActivateAppResponse> {
  return apiFetch<ActivateAppResponse>(`/apps/manifests/${encodeURIComponent(id)}/activate`, { method: 'POST' });
}

export async function disableAppVersion(id: string): Promise<TenantAppRecord> {
  const response = await apiFetch<{ app: TenantAppRecord }>(`/apps/manifests/${encodeURIComponent(id)}/disable`, { method: 'POST' });
  return response.app;
}

/** What to tell the admin after an activation: the version that went live, what it replaced and any picklist changes. */
export function describeActivation(result: ActivateAppResponse): string {
  const parts = [`Version ${result.app.version} of ${result.app.key} is now active`];
  if (result.replacedVersion !== null) parts.push(`replacing version ${result.replacedVersion}`);
  if (result.picklistsCreated.length > 0) parts.push(`picklists created: ${result.picklistsCreated.join(', ')}`);
  const extended = Object.entries(result.picklistsExtended).map(([name, items]) => `${name} (+${items.join(', ')})`);
  if (extended.length > 0) parts.push(`picklists extended: ${extended.join('; ')}`);
  return `${parts.join('; ')}.`;
}
